import {useEffect, useMemo, useState} from "react";
import {API} from "../API/api";
import styled from "styled-components";
import {Colors} from "../Styles/Colors";

type ShipTab = "all" | "mine";

const MY_SHIPS_STORAGE_KEY = "my_ships";

type LocalizedText = {
	en_EN?: string;
	de_DE?: string;
	zh_CN?: string;
	fr_FR?: string;
};

type ShipInGamePrice = {
	slug: string;
	price_auec: number;
};

type Ship = {
	id: number;
	name: string;
	slug: string;
	manufacturer?: {
		code?: string;
		name?: string;
	};
	type?: LocalizedText;
	size?: LocalizedText;
	description?: LocalizedText | null;
	production_status?: LocalizedText;
	production_note?: LocalizedText;
	foci?: LocalizedText[];
	dimension?: {
		length?: number | null;
		width?: number | null;
		height?: number | null;
	};
	mass?: number | null;
	cargo_capacity?: number | null;
	crew?: {
		min?: number | null;
		max?: number | null;
	};
	speed?: {
		scm?: number | null;
		max?: number | null;
	};
	agility?: {
		pitch?: number | null;
		yaw?: number | null;
		roll?: number | null;
	};
	msrp?: number | null;
	pledge_url?: string;
	loaner?: {
		name: string;
		slug: string;
	}[];
	updated_at?: string;
	inGamePrice?: number;
};

const Styles = {
	Page: styled.div`
		padding: 28px;
		color: ${Colors.raw.terminal_light};
	`,

	Header: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		gap: 16px;
		margin-bottom: 22px;

		@media (max-width: 760px) {
			align-items: flex-start;
			flex-direction: column;
		}
	`,

	TitleBlock: styled.div`
		display: grid;
		gap: 8px;
	`,

	Eyebrow: styled.div`
		color: ${Colors.raw.neon_orange};
		letter-spacing: 3px;
		text-transform: uppercase;
		font-size: 12px;
	`,

	Title: styled.h1`
		margin: 0;
		font-size: clamp(28px, 5vw, 48px);
		line-height: 1;
		text-transform: uppercase;
		text-shadow: 0 0 18px rgba(255, 122, 0, 0.45);
	`,

	Subline: styled.div`
		opacity: 0.72;
		font-size: 13px;
	`,

	Counter: styled.div`
		border: 1px solid rgba(255, 153, 51, 0.38);
		background: rgba(255, 122, 0, 0.08);
		padding: 10px 14px;
		font-family: "Courier New", monospace;
		font-size: 13px;
		color: ${Colors.raw.neon_orange};
		box-shadow: 0 0 16px rgba(255, 122, 0, 0.12);
	`,

	Controls: styled.div`
		display: grid;
		grid-template-columns: 1fr auto;
		gap: 14px;
		align-items: center;
		margin-bottom: 22px;

		@media (max-width: 760px) {
			grid-template-columns: 1fr;
		}
	`,

	SearchInput: styled.input`
		width: 100%;
		box-sizing: border-box;
		border: 1px solid rgba(255, 153, 51, 0.38);
		background: rgba(0, 0, 0, 0.35);
		color: ${Colors.raw.terminal_light};
		padding: 12px 14px;
		outline: none;
		font-family: "Courier New", monospace;

		&::placeholder {
			color: rgba(255, 255, 255, 0.45);
		}

		&:focus {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 0 2px rgba(255, 122, 0, 0.16);
		}
	`,

	Tabs: styled.div`
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
	`,

	TabButton: styled.button<{ $active: boolean }>`
		border: 1px solid ${({ $active }) =>
		$active ? Colors.raw.neon_orange : "rgba(255, 153, 51, 0.32)"};
		background: ${({ $active }) =>
		$active ? "rgba(255, 122, 0, 0.22)" : "rgba(0, 0, 0, 0.25)"};
		color: ${({ $active }) =>
		$active ? Colors.raw.neon_orange : Colors.raw.terminal_light};
		padding: 10px 14px;
		cursor: pointer;
		font-family: "Courier New", monospace;
		font-size: 12px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 1px;

		&:hover {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 12px rgba(255, 122, 0, 0.22);
		}
	`,

	Grid: styled.div`
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
		gap: 18px;
	`,

	Card: styled.article`
		position: relative;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		gap: 14px;
		padding: 18px;

		border: 1px solid rgba(255, 153, 51, 0.38);
		background:
			radial-gradient(circle at top right, rgba(255, 122, 0, 0.20), transparent 36%),
			linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018));
		box-shadow: 0 0 20px rgba(255, 122, 0, 0.10);
		text-align: left;
		clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 0 100%);

		&::before {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			height: 3px;
			width: 100%;
			background: ${Colors.raw.neon_orange};
		}
	`,

	Top: styled.div`
		display: flex;
		justify-content: space-between;
		gap: 12px;
		align-items: flex-start;
	`,

	NameBlock: styled.div`
		min-width: 0;
	`,

	ShipName: styled.h2`
		margin: 0;
		font-size: 21px;
		line-height: 1.15;
		color: ${Colors.raw.terminal_light};
	`,

	Manufacturer: styled.div`
		margin-top: 5px;
		color: ${Colors.raw.neon_orange};
		font-size: 12px;
		letter-spacing: 1.8px;
		text-transform: uppercase;
	`,

	Price: styled.div`
		flex-shrink: 0;
		border: 1px solid rgba(67, 255, 157, 0.45);
		background: rgba(67, 255, 157, 0.10);
		color: #8dffc5;
		padding: 7px 9px;
		font-size: 12px;
		font-weight: 700;
		white-space: nowrap;
	`,

	Tags: styled.div`
		display: flex;
		gap: 8px;
		flex-wrap: wrap;
	`,

	Tag: styled.span`
		border: 1px solid rgba(255, 255, 255, 0.18);
		background: rgba(0, 0, 0, 0.20);
		color: ${Colors.raw.terminal_light};
		padding: 5px 8px;
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	TagHighlight: styled.span`
		border: 1px solid rgba(255, 153, 51, 0.55);
		background: rgba(255, 122, 0, 0.14);
		color: ${Colors.raw.neon_orange};
		padding: 5px 8px;
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	Description: styled.p`
		margin: 0;
		opacity: 0.78;
		font-size: 13px;
		line-height: 1.45;
		display: -webkit-box;
		-webkit-line-clamp: 5;
		-webkit-box-orient: vertical;
		overflow: hidden;
	`,

	Stats: styled.div`
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 8px;

		@media (max-width: 520px) {
			grid-template-columns: repeat(2, 1fr);
		}
	`,

	Stat: styled.div`
		border: 1px solid rgba(255, 255, 255, 0.12);
		background: rgba(0, 0, 0, 0.22);
		padding: 10px;
		min-width: 0;
	`,

	StatLabel: styled.div`
		opacity: 0.58;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 1.4px;
		margin-bottom: 5px;
	`,

	StatValue: styled.div`
		color: ${Colors.raw.terminal_light};
		font-size: 14px;
		font-weight: 800;
		word-break: break-word;
	`,

	Footer: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        margin-top: auto;
        padding-top: 4px;
	`,

	CardActions: styled.div`
		display: flex;
		gap: 10px;
		flex-wrap: wrap;
		align-items: center;
		justify-content: flex-end;
	`,

	MarkButton: styled.button<{ $active: boolean }>`
		border: 1px solid ${({ $active }) =>
		$active ? "#43ff9d" : "rgba(255, 153, 51, 0.55)"};
		background: ${({ $active }) =>
		$active ? "rgba(67, 255, 157, 0.12)" : "rgba(255, 122, 0, 0.14)"};
		color: ${({ $active }) =>
		$active ? "#8dffc5" : Colors.raw.neon_orange};
		padding: 8px 10px;
		cursor: pointer;
		font-size: 11px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 1px;

		&:hover {
			box-shadow: ${({ $active }) =>
		$active
			? "0 0 12px rgba(67, 255, 157, 0.22)"
			: "0 0 12px rgba(255, 122, 0, 0.24)"};
		}
	`,

	Updated: styled.div`
        opacity: 0.48;
        font-size: 11px;
	`,

	Link: styled.a`
		color: ${Colors.raw.neon_orange};
		font-size: 12px;
		font-weight: 700;
		text-decoration: none;
		text-transform: uppercase;
		letter-spacing: 1px;

		&:hover {
			text-decoration: underline;
			text-shadow: 0 0 10px rgba(255, 122, 0, 0.55);
		}
	`,

	Loading: styled.div`
        padding: 28px;
        border: 1px dashed rgba(255, 255, 255, 0.22);
        opacity: 0.75;
        text-align: center;
	`,

	EmptyState: styled.div`
		padding: 30px;
		border: 1px dashed rgba(255, 255, 255, 0.22);
		background: rgba(255, 255, 255, 0.035);
		opacity: 0.78;
		text-align: center;
	`,
};

const localize = (value?: LocalizedText | null) =>
	value?.de_DE || value?.en_EN || "-";

const formatNumber = (value?: number | null, suffix = "") =>
	value === null || value === undefined
		? "-"
		: `${new Intl.NumberFormat("de-DE").format(value)}${suffix}`;

const formatPrice = (value?: number | null) =>
	value === null || value === undefined
		? "n/a"
		: `$${new Intl.NumberFormat("de-DE").format(value)}`;

const formatCrew = (ship: Ship) => {
	const min = ship.crew?.min;
	const max = ship.crew?.max;

	if (min === undefined || min === null || max === undefined || max === null) {
		return "-";
	}

	return min === max ? `${min}` : `${min}–${max}`;
};

const formatDate = (value?: string) => {
	if (!value) {
		return "";
	}

	return new Date(value).toLocaleDateString("de-DE");
};

const ShipCard = ({
					  ship,
					  isMine,
					  showMarkButton,
					  onToggleMine,
				  }: {
	ship: Ship;
	isMine: boolean;
	showMarkButton: boolean;
	onToggleMine: (shipId: number) => void;
}) => {
	const foci = ship.foci?.map(localize).filter(Boolean) ?? [];
	const description = localize(ship.description);

	console.log( ship.inGamePrice )

	return (
		<Styles.Card>
			<Styles.Top>
				<Styles.NameBlock>
					<Styles.ShipName>{ship.name}</Styles.ShipName>
					<Styles.Manufacturer>
						{ship.manufacturer?.name ?? "Unbekannter Hersteller"}
					</Styles.Manufacturer>
				</Styles.NameBlock>

				<Styles.Price>{formatPrice(ship.msrp)}</Styles.Price>
			</Styles.Top>

			<Styles.Tags>
				<Styles.TagHighlight>{localize(ship.type)}</Styles.TagHighlight>
				<Styles.Tag>{localize(ship.size)}</Styles.Tag>
				<Styles.Tag>{localize(ship.production_status)}</Styles.Tag>
				{foci.map(focus => (
					<Styles.Tag key={focus}>{focus}</Styles.Tag>
				))}
			</Styles.Tags>

			<div>{ ship.inGamePrice ? (ship.inGamePrice) + ' aUEC' : "" }</div>

			<div>
				<img width={'100%'} src={'/images/Ships/'+ ship.slug+'.png'} alt={ship.name} />
			</div>

			{description !== "-" && (
				<Styles.Description>{description}</Styles.Description>
			)}

			<Styles.Stats>
				<Styles.Stat>
					<Styles.StatLabel>Crew</Styles.StatLabel>
					<Styles.StatValue>{formatCrew(ship)}</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>Cargo</Styles.StatLabel>
					<Styles.StatValue>
						{formatNumber(ship.cargo_capacity, " SCU")}
					</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>Masse</Styles.StatLabel>
					<Styles.StatValue>{formatNumber(ship.mass, " kg")}</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>Länge</Styles.StatLabel>
					<Styles.StatValue>
						{formatNumber(ship.dimension?.length, " m")}
					</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>Breite</Styles.StatLabel>
					<Styles.StatValue>
						{formatNumber(ship.dimension?.width, " m")}
					</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>Höhe</Styles.StatLabel>
					<Styles.StatValue>
						{formatNumber(ship.dimension?.height, " m")}
					</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>SCM</Styles.StatLabel>
					<Styles.StatValue>{ ship.speed?.scm && formatNumber(ship.speed?.scm * 3.6, " km/h")}</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>Max Speed</Styles.StatLabel>
					<Styles.StatValue>{ ship.speed?.max && formatNumber(ship.speed?.max * 3.6, " km/h")}</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>Loaner</Styles.StatLabel>
					<Styles.StatValue>
						{ship.loaner && ship.loaner.length > 0
							? ship.loaner.map(loaner => loaner.name).join(", ")
							: "-"}
					</Styles.StatValue>
				</Styles.Stat>
			</Styles.Stats>

			<Styles.Footer>
				<Styles.Updated>
					{ship.updated_at ? `Aktualisiert: ${formatDate(ship.updated_at)}` : ""}
				</Styles.Updated>

				<Styles.CardActions>
					{showMarkButton && (
						<Styles.MarkButton
							type="button"
							$active={isMine}
							onClick={() => onToggleMine(ship.id)}
						>
							{isMine ? "Gehört mir" : "Als meins markieren"}
						</Styles.MarkButton>
					)}

					{ship.pledge_url && (
						<Styles.Link
							href={ship.pledge_url}
							target="_blank"
							rel="noreferrer"
						>
							Pledge öffnen
						</Styles.Link>
					)}
				</Styles.CardActions>
			</Styles.Footer>
		</Styles.Card>
	);
};

export const Ships = ({ filter } :{ filter?: ShipTab }) => {

	const [ships, setShips] = useState<Ship[]>([]);
	const [prices, setPrices] = useState<ShipInGamePrice[]>([]);
	const [nextParam, setNextParam] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [activeTab, setActiveTab] = useState<ShipTab>( filter ?? 'all' );
	const [myShipIds, setMyShipIds] = useState<number[]>(() => {
		const stored = localStorage.getItem(MY_SHIPS_STORAGE_KEY);

		if (!stored) {
			return [];
		}

		try {
			const parsed = JSON.parse(stored);

			return Array.isArray(parsed)
				? parsed.filter((id): id is number => typeof id === "number")
				: [];
		} catch {
			return [];
		}
	});

	const fetch = {
		prices: () => {
			API.getShipPrices()
				.then(response => { setPrices(response.data.ships) });
		},
		ships: (param: string) => {
			setIsLoading(true);

			API.getShips(param)
				.then(response => {
					const responseShips = response.data.data as Ship[];

					setShips(prev => {
						const uniqueShips = new Map<number, Ship>();

						prev.forEach(ship => {
							uniqueShips.set(ship.id, ship);
						});

						responseShips.forEach(ship => {
							uniqueShips.set(ship.id, ship);
						});

						return Array.from(uniqueShips.values());
					});

					if (response.data.links.next) {
						setNextParam(response.data.links.next);
					} else {
						setNextParam("");
					}
				})
				.finally(() => {
					setIsLoading(false);
				});
		},
	};

	useEffect(() => {
		fetch.ships("");
		fetch.prices();
	}, []);

	useEffect(() => {
		if (nextParam !== "") {
			fetch.ships(nextParam);
		}
	}, [nextParam]);

	console.log( prices )

	useEffect(() => {
		localStorage.setItem(MY_SHIPS_STORAGE_KEY, JSON.stringify(myShipIds));
	}, [myShipIds]);

	const handles = {
		toggleMine: (shipId: number) => {
			setMyShipIds(prev =>
				prev.includes(shipId)
					? prev.filter(id => id !== shipId)
					: [...prev, shipId]
			);
		},
	};

	const sortedShips = useMemo(
		() =>
			[...ships]
				.map( p => ({
					...p,
					inGamePrice: prices.find( price => price.slug === p.slug )?.price_auec
				}))
				.filter( p => activeTab === "mine" ? myShipIds.includes( p.id ) : true )
				.sort((a, b) =>
				a.name.localeCompare(b.name, "de")
			),
		[ships, activeTab, prices]
	);

	const visibleShips = useMemo(() => {
		const normalizedSearchTerm = searchTerm.trim().toLowerCase();

		return sortedShips
			.filter(ship => {
				if (activeTab === "mine") {
					return myShipIds.includes(ship.id);
				}

				return true;
			})
			.filter(ship => {
				if (!normalizedSearchTerm) {
					return true;
				}

				const name = ship.name.toLowerCase();
				const manufacturer = ship.manufacturer?.name?.toLowerCase() ?? "";
				const manufacturerCode = ship.manufacturer?.code?.toLowerCase() ?? "";

				return (
					name.includes(normalizedSearchTerm) ||
					manufacturer.includes(normalizedSearchTerm) ||
					manufacturerCode.includes(normalizedSearchTerm)
				);
			});
	}, [activeTab, myShipIds, searchTerm, sortedShips]);

	return (
		<Styles.Page>
			<Styles.Header>
				<Styles.TitleBlock>
					<Styles.Eyebrow>Ship Matrix</Styles.Eyebrow>
					<Styles.Title>Schiffe</Styles.Title>
					<Styles.Subline>
						Technische Übersicht der verfügbaren Star-Citizen-Schiffe.
					</Styles.Subline>
				</Styles.TitleBlock>

				<Styles.Counter>
					{visibleShips.length} / {ships.length} Schiffe
				</Styles.Counter>
			</Styles.Header>

			<Styles.Controls>
				<Styles.SearchInput
					value={searchTerm}
					placeholder="Nach Name oder Hersteller suchen..."
					onChange={event => setSearchTerm(event.target.value)}
				/>

				<Styles.Tabs>
					<Styles.TabButton
						type="button"
						$active={activeTab === "all"}
						onClick={() => setActiveTab("all")}
					>
						Alle Schiffe
					</Styles.TabButton>

					<Styles.TabButton
						type="button"
						$active={activeTab === "mine"}
						onClick={() => setActiveTab("mine")}
					>
						Meine Schiffe ({myShipIds.length})
					</Styles.TabButton>
				</Styles.Tabs>
			</Styles.Controls>

			{ships.length === 0 && isLoading ? (
				<Styles.Loading>Schiffe werden geladen...</Styles.Loading>
			) : visibleShips.length === 0 ? (
				<Styles.EmptyState>
					{activeTab === "mine"
						? "Du hast noch keine Schiffe markiert."
						: "Keine Schiffe für diese Suche gefunden."}
				</Styles.EmptyState>
			) : (
				<Styles.Grid>
					{visibleShips.map(ship => (
						<ShipCard
							key={ship.id}
							ship={ship}
							isMine={myShipIds.includes(ship.id)}
							showMarkButton={activeTab === "all"}
							onToggleMine={handles.toggleMine}
						/>
					))}
				</Styles.Grid>
			)}
		</Styles.Page>
	);
};